import { CONTACT_MESSAGE_STATUS, WAITING_LIST_SOURCE } from "@clinic/shared";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Icon,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  type IconName,
} from "@clinic/ui";
import { useWaitingList } from "@web/modules/appointments/queries";
import { useSession } from "@web/shared/providers/session";
import { canSeeBilling } from "@web/shared/permissions/billing";
import { usePendingBookings } from "@web/shared/queries/booking";
import { seesPendingBookings } from "@web/shared/permissions/booking";
import { seesContactMessages } from "@web/shared/permissions/website";
import { useContactMessages } from "@web/shared/queries/website";
import { useInventoryAlerts } from "@web/modules/inventory/queries";
import { useLabOrders } from "@web/modules/labs/queries";
import { usePatients } from "@web/shared/queries/patients";
import { useSeenNotifications } from "@web/shared/hooks/use-seen-notifications";
import { cn } from "@clinic/ui/lib/cn";

interface Waiting {
  readonly key: string;
  readonly icon: IconName;
  readonly count: number | undefined;
  readonly to: string;
}

export function NotificationBell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, can } = useSession();

  const frontDesk = seesPendingBookings(can);
  const pending = usePendingBookings({ limit: 1 }, frontDesk);

  const mayMessages = seesContactMessages(can);
  const messages = useContactMessages(
    { limit: 1, status: CONTACT_MESSAGE_STATUS.OPEN },
    mayMessages,
  );

  const mayQueue = can("waiting-list.list");
  const urgent = useWaitingList({ limit: 1, source: WAITING_LIST_SOURCE.ONLINE }, mayQueue);

  const mayLabs = can("lab-orders.list");
  const overdueLabs = useLabOrders({ limit: 1, overdue: true }, mayLabs);

  const mayStock = can("inventory.alerts");
  const alerts = useInventoryAlerts(mayStock);

  const seesMoney = user !== null && canSeeBilling(can);
  const owing = usePatients({ page: 1, limit: 1, hasBalance: true }, { enabled: seesMoney });

  const stock =
    alerts.data &&
    alerts.data.low.length + alerts.data.expiring.length + alerts.data.expired.length;

  const entries: Waiting[] = [
    {
      key: "pendingBookings",
      icon: "globe",
      count: frontDesk ? pending.data?.total : 0,
      to: "/appointments?status=pending",
    },
    {
      key: "contactMessages",
      icon: "message",
      count: mayMessages ? messages.data?.total : 0,
      to: "/settings?view=website&tab=messages&status=open",
    },
    {
      key: "urgentRequests",
      icon: "alert",
      count: mayQueue ? urgent.data?.total : 0,
      to: "/appointments",
    },
    {
      key: "overdueLabs",
      icon: "clipboard",
      count: mayLabs ? overdueLabs.data?.total : 0,
      to: "/labs?tab=orders",
    },
    { key: "inventoryAlerts", icon: "package", count: mayStock ? stock : 0, to: "/inventory" },
    {
      key: "overdueBalances",
      icon: "money",
      count: seesMoney ? owing.data?.total : 0,
      to: "/patients?filter=balance",
    },
  ];

  const waiting = entries.filter((entry) => (entry.count ?? 0) > 0);
  const { unseen: total, markAllRead } = useSeenNotifications(
    user?.id ?? null,
    Object.fromEntries(entries.map((entry) => [entry.key, entry.count])),
  );

  return (
    <Menu>
      <MenuTrigger
        data-testid="notification-bell"
        aria-label={
          total > 0 ? t("nav.notifications", { count: total }) : t("nav.notificationsEmpty")
        }
        className={cn(
          "relative inline-flex size-(--control-h) cursor-pointer items-center justify-center lg:size-(--control-h-sm)",
          "rounded-control border border-line bg-surface text-ink-muted",
          "transition-colors duration-[250ms] ease-in-out hover:bg-primary-100 hover:text-primary-700",
          "data-[state=open]:border-primary-600 data-[state=open]:text-primary-700",
        )}
      >
        <Icon name="bell" />

        {total > 0 && (
          <span
            data-part="notification-count"
            data-testid="notification-count"
            aria-hidden="true"
            className={cn(
              "absolute -top-1 -end-1 inline-flex min-w-4 items-center justify-center rounded-pill",
              "bg-danger-600 px-1 text-micro font-medium text-ink-inverse tabular-nums",
            )}
          >
            {total}
          </span>
        )}
      </MenuTrigger>

      <MenuContent data-testid="notification-menu" className="min-w-72">
        {waiting.length === 0 ? (
          <p data-testid="notification-empty" className="px-3 py-2 text-label text-ink-muted">
            {t("nav.notificationsEmpty")}
          </p>
        ) : (
          <>
            {waiting.map((entry) => (
              <MenuItem
                key={entry.key}
                data-testid={`notification-${entry.key}`}
                icon={entry.icon}
                onSelect={() => void navigate(entry.to)}
                trailing={
                  <span className="text-label tabular-nums text-ink-muted">{entry.count}</span>
                }
              >
                {t(`notifications.${entry.key}`)}
              </MenuItem>
            ))}
            <MenuSeparator />
            <MenuItem
              data-testid="notification-mark-all-read"
              icon="check"
              unavailable={total === 0}
              onSelect={markAllRead}
            >
              {t("notifications.markAllRead")}
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}
